import React, { useState } from 'react';
import {
  Button,
  Typography,
  Space,
  Card,
  Dropdown,
  Flex,
  QRCode,
  Layout,
  Tag,
  Divider,
  MenuProps
} from 'antd';
import {
  AppleFilled,
  AndroidFilled,
  CloudDownloadOutlined,
  ThunderboltFilled,
  GlobalOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import useSWR from 'swr';
import Loading from './Loading';

import { Helmet } from 'react-helmet-async';

const { Title, Text, Paragraph } = Typography;
const { Content, Footer } = Layout;

// --- 配置区域：请替换为你的真实链接 ---
const APP_CONFIG = {
  logo: "/logo1024.png", // 替换你的LOGO地址
  name: "狐哩狐哩",
  slogan: "恭喜你发现了神秘小镇",
  version: "V 1.0.1",
  updateDate: "2026.01.05",
  iosLink: "https://apps.apple.com/cn/app/id6756485994", // App Store 链接
  androidDirectLink: "https://dd.lynne.com.cn/app-1.0.1.apk", // 服务器直链
  androidCloudLink: "https://dd.lynne.com.cn/app-1.0.1.apk", // 网盘链接
};

const fetcher = async (url: string) => {
    const res = await fetch(url, {
        headers: new Headers({
            'Access-Control-Allow-Origin': '*',
        }),
    });
    const json = await res.json();
    if (!json.success) {
        const error = new Error((await json).message);
        throw error;
    }
    return json;
};

const DownloadPage = () => {
  // 控制移动端/PC端展示逻辑（简单的断点演示，实际项目可用 useMediaQuery）
  const [showQR, setShowQR] = useState(false);
  const { data, error, isLoading } = useSWR(
        'https://api.lynne.com.cn/app/version',
        fetcher
    );
    if (isLoading || data === undefined) return <Loading />;
    if (error) return <div>居然加载失败了喵！</div>;

  // 安卓下载选项菜单
  const androidItems: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <a href={data.data.android_url_cloud || data.data.android_url} target="_blank" rel="noreferrer" style={{ padding: '4px 0', display: 'block' }}>
          <Space>
            <CloudDownloadOutlined />
            <span>网盘下载</span>
          </Space>
        </a>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', overflow: 'hidden', position: 'relative' }}>
      {/* 1. 动态渐变背景 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          backgroundImage: `
            radial-gradient(at 0% 0%, hsla(253,16%,7%,1) 0, transparent 50%), 
            radial-gradient(at 50% 0%, hsla(225,39%,30%,1) 0, transparent 50%), 
            radial-gradient(at 100% 0%, hsla(339,49%,30%,1) 0, transparent 50%)
          `,
          backgroundSize: '200% 200%',
          animation: 'gradientBG 15s ease infinite',
        }}
      />

      <Helmet>
          <title>狐哩狐哩APP - 玲奈的dd站</title>
          <meta
              name="description"
              content="下载狐哩狐哩，解锁云上小镇"
              />  
          <meta
              name="keywords"
              content="狐哩狐哩,APP,星海玲奈,小镇"
              />  
      </Helmet>
      
      {/* 注入简单的 CSS 动画 */}
      <style>{`
        @keyframes gradientBG {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
        }
      `}</style>

      <Content style={{ zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
        <Flex gap={40} wrap="wrap" justify="center" align="center" style={{ maxWidth: 1200, width: '100%' }}>

          {/* 右侧：下载卡片 */}
          <Card 
            className="glass-card"
            bordered={false}
            style={{ 
              width: '100%', 
              maxWidth: 480, 
              borderRadius: 24,
              padding: '20px 10px'
            }}
          >
            <Flex vertical align="center" gap={10}>
              {/* App Logo */}
              <img 
                src={APP_CONFIG.logo} 
                alt="Logo" 
                style={{ 
                  width: 96, 
                  height: 96, 
                  borderRadius: 22, 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  marginBottom: 10
                }} 
              />
              
              {/* 标题与标语 */}
              <div style={{ textAlign: 'center' }}>
                <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#333' }}>
                  {APP_CONFIG.name}
                </Title>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  {APP_CONFIG.slogan}
                </Text>
              </div>

              {/* 版本信息标签 */}
              <Space>
                <Tag color="blue">{ data.data.version}</Tag>
              </Space>

              <Divider style={{ margin: '20px 0' }} />

              {/* 下载按钮区域 */}
              <Flex vertical gap={16} style={{ width: '100%' }}>
                
                {/* 1. iOS 下载 */}
                <Button 
                  type="primary" 
                  shape="round" 
                  size="large" 
                  icon={<AppleFilled style={{ fontSize: 20 }} />}
                  style={{ 
                    height: 56, 
                    fontSize: 18, 
                    backgroundColor: '#000', // 苹果黑
                    borderColor: '#000',
                    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.39)'
                  }}
                  href={APP_CONFIG.iosLink}
                  target="_blank"
                >
                  App Store 下载
                </Button>

                {/* 2. Android 下载 (组合按钮) */}
                {/* 使用 Dropdown.Button 实现主次操作分离 */}
                <Dropdown.Button
                  type="primary"
                  size="large"
                  icon={<CloudDownloadOutlined />}
                  menu={{ items: androidItems }}
                  onClick={() => window.open( data.data.android_url, '_blank')}
                  style={{ width: '100%' }}
                  buttonsRender={([leftButton, rightButton]) => [
                    <Button 
                        key="left"
                        type="primary" 
                        shape="round" 
                        size="large"
                        icon={<AndroidFilled style={{ fontSize: 20 }} />}
                        style={{ 
                          flex: 1, 
                          height: 56, 
                          fontSize: 18,
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          background: 'linear-gradient(45deg, #11998e 0%, #38ef7d 100%)', // 安卓绿渐变
                          border: 'none',
                          boxShadow: '0 4px 14px 0 rgba(56, 239, 125, 0.4)'
                        }}
                        onClick={() => window.open(data.data.android_url, '_blank')}
                    >
                      Android 下载
                    </Button>,
                    React.cloneElement(rightButton as React.ReactElement<any>, {
                        key: "right",
                        size: 'large',
                        style: { 
                            height: 56, 
                            background: '#11998e', // 匹配渐变深色
                            border: 'none',
                            color: '#fff',
                            borderTopRightRadius: 30,
                            borderBottomRightRadius: 30
                        }
                    }),
                  ]}
                >
                    {/* 这个children不会被渲染，因为自定义了buttonsRender，但保留为空即可 */}
                </Dropdown.Button>
                
                <Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
                  *安卓如遇下载慢请尝试右侧网盘下载链接
                </Text>
              </Flex>

              <Divider plain style={{ fontSize: 12, color: '#999' }}>或者</Divider>

              {/* 扫码下载区域 (点击展开或一直显示) */}
              <div 
                style={{ 
                    background: '#fff', 
                    padding: 16, 
                    borderRadius: 16, 
                    boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)',
                    cursor: 'pointer'
                }}
                onClick={() => setShowQR(!showQR)}
              >
                 <Space direction="vertical" align="center">
                    <QRCode 
                        value={window.location.href} 
                        icon={APP_CONFIG.logo}
                        size={160}
                        color="#11998e" // 使用主题色
                    />
                    <Space size={4}>
                        <QrcodeOutlined />
                        <Text type="secondary">手机扫码下载</Text>
                    </Space>
                 </Space>
              </div>

            </Flex>
          </Card>
        </Flex>
      </Content>

      <Footer style={{ background: 'transparent', textAlign: 'center', zIndex: 1 }}>
        © {new Date().getFullYear()} {APP_CONFIG.name} All Rights Reserved.<a href="https://beian.miit.gov.cn/">京ICP备2024099964号-2</a>
      </Footer>
    </Layout>
  );
};

export default DownloadPage;